// src/components/PhotoAnnotatorButton.jsx
import React, { useRef, useState } from "react";
import { PhotoAnnotatorModal } from "./PhotoAnnotatorModal";
import "../styles/photo-annotator.css";

// icons
import { Image } from "lucide-react";

/**
 * Plug-and-play photo annotator button + thumbnail list.
 *
 * Props:
 *  - label?: string
 *  - onImagesChange?: (images: KeptImage[]) => void
 *      KeptImage = { id, blob, dataUrl, fileName }
 *  - emojis?: string[]
 */
export function PhotoAnnotatorButton({
  label = "Choose or take a photo",
  onImagesChange,
  emojis,
}) {
  const fileInputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [fileName, setFileName] = useState("annotated-photo.png");

  // Images that have been "kept" from the modal
  const [savedImages, setSavedImages] = useState([]);

  const openFileDialog = () => {
    console.log("[PhotoAnnotatorButton] openFileDialog");
    if (!fileInputRef.current) return;
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    console.log("[PhotoAnnotatorButton] handleFileChange", e.target.files);
    const file = e.target.files?.[0];
    if (!file) {
      console.log("[PhotoAnnotatorButton] no file selected");
      return;
    }

    const url = URL.createObjectURL(file);
    console.log("[PhotoAnnotatorButton] created object URL:", url);

    setImageUrl(url);
    setFileName(file.name ? `annotated-${file.name}` : "annotated-photo.png");
    setIsOpen(true);
  };

  const resetInput = () => {
    console.log("[PhotoAnnotatorButton] resetInput");
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(null);
    setFileName("annotated-photo.png");
    setIsOpen(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleModalClose = () => {
    console.log("[PhotoAnnotatorButton] handleModalClose");
    resetInput();
  };

  // Called when user presses "Keep" in the modal
  const handleKeepImage = async ({ blob, dataUrl, fileName }) => {
    console.log("[PhotoAnnotatorButton] handleKeepImage", { blob, fileName });

    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const nextImages = [...savedImages, { id, blob, dataUrl, fileName }];
    setSavedImages(nextImages);

    // Notify parent form if needed
    if (onImagesChange) {
      onImagesChange(nextImages);
    }

    // Close modal & reset file input
    resetInput();
  };

  const handleRemoveImage = (id) => {
    const nextImages = savedImages.filter((img) => img.id !== id);
    setSavedImages(nextImages);
    if (onImagesChange) {
      onImagesChange(nextImages);
    }
  };

  return (
    <div className="photo-annotator-wrapper mb-3">
      <button
        type="button"
        className="photo-annotator-trigger-button"
        onClick={openFileDialog}
      >
        <Image size={24} /> {label}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        // capture="environment"    // keep commented out so user gets camera/library chooser
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {/* Modal */}
      {isOpen && imageUrl && (
        <PhotoAnnotatorModal
          isOpen={isOpen}
          imageUrl={imageUrl}
          fileName={fileName}
          onKeep={handleKeepImage} // 👈 changed
          onRequestClose={handleModalClose}
          emojis={emojis}
        />
      )}

      {/* Thumbnails list */}
      {savedImages.length > 0 && (
        <div className="photo-annotator-thumbnails">
          {savedImages.map((img) => (
            <div key={img.id} className="photo-annotator-thumbnail">
              <button
                type="button"
                className="photo-annotator-thumbnail-remove"
                onClick={() => handleRemoveImage(img.id)}
                aria-label="Remove image"
              >
                ×
              </button>
              <img
                src={img.dataUrl}
                alt={img.fileName || "Selected image"}
                className="photo-annotator-thumbnail-image"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// previous
// // src/components/PhotoAnnotatorButton.jsx
// import { useRef, useState } from "react";
// import { PhotoAnnotatorModal } from "./PhotoAnnotatorModal";
// import "../styles/photo-annotator.css";

// // icons
// import { Image } from "lucide-react";

// /**
//  * Plug-and-play photo annotator button.
//  *
//  * Props:
//  *  - label?: string
//  *  - onSave?: ({ blob, dataUrl, fileName }) => void | Promise<void>
//  *  - emojis?: string[]
//  *  - captureCameraOnly?: boolean
//  */
// export function PhotoAnnotatorButton({
//   label = "Choose or take a photo",
//   onSave,
//   emojis,
//   captureCameraOnly = false, // if true does not allow option to select from library or file
// }) {
//   const fileInputRef = useRef(null);
//   const [isOpen, setIsOpen] = useState(false);
//   const [imageUrl, setImageUrl] = useState(null);
//   const [fileName, setFileName] = useState("annotated-photo.png");

//   const openFileDialog = () => {
//     fileInputRef.current?.click();
//   };

//   const handleFileChange = (e) => {
//     const file = e.target.files?.[0];
//     if (!file) return;

//     const url = URL.createObjectURL(file);
//     setImageUrl(url);
//     setFileName(file.name ? `annotated-${file.name}` : "annotated-photo.png");
//     setIsOpen(true);
//   };

//   const resetInput = () => {
//     if (imageUrl) {
//       URL.revokeObjectURL(imageUrl);
//     }
//     setImageUrl(null);
//     setFileName("annotated-photo.png");
//     setIsOpen(false);

//     if (fileInputRef.current) {
//       fileInputRef.current.value = "";
//     }
//   };

//   const handleModalClose = () => {
//     resetInput();
//   };

//   return (
//     <>
//       <button
//         type="button"
//         className="photo-annotator-trigger-button"
//         onClick={openFileDialog}
//       >
//         <Image size={32} /> {label}
//       </button>

//       <input
//         ref={fileInputRef}
//         type="file"
//         accept="image/*"
//         {...(captureCameraOnly ? { capture: "environment" } : {})}
//         style={{ display: "none" }}
//         onChange={handleFileChange}
//       />

//       {isOpen && imageUrl && (
//         <PhotoAnnotatorModal
//           isOpen={isOpen}
//           imageUrl={imageUrl}
//           fileName={fileName}
//           onSave={onSave}
//           onRequestClose={handleModalClose}
//           emojis={emojis}
//         />
//       )}
//     </>
//   );
// }
